<template>
  <div class="flex h-screen bg-gray-50">
    <SideBar class="flex-shrink-0" />

    <div class="flex-1 overflow-auto relative">
      <div class="h-full">
        <div class="mx-auto flex flex-col items-center">
          <!-- Header + Controls -->
          <div class="mb-4 mt-6 w-full max-w-7xl mx-auto px-4">
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
              <!-- left spacer -->
              <div class="hidden sm:block"></div>

              <!-- center title -->
              <div class="hidden sm:block text-center">
                <h1 class="text-4xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-1">
                  Todos
                </h1>
                <p class="text-gray-600 text-lg">Organize your workflow with style and efficiency</p>
              </div>

              <!-- right bell -->
              <div class="flex justify-end">
                <button
                  @click="drawerOpen = !drawerOpen"
                  class="relative p-2 rounded-full hover:bg-gray-100 transition"
                >
                  <svg class="w-6 h-6 text-black" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M15 17h5l-1.405-1.405A2.032 2.032 0 
                        0118 14.158V11c0-3.07-1.64-5.64-4.5-6.32V4a1.5 
                        1.5 0 10-3 0v.68C7.64 5.36 6 7.93 6 
                        11v3.159c0 .538-.214 1.055-.595 1.436L4 
                        17h5m6 0v1a3 3 0 11-6 0v-1m6 
                        0H9" />
                  </svg>
                  <span
                    v-if="unreadCount"
                    class="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full"
                  >{{ unreadCount }}</span>
                </button>
              </div>
            </div>

            <!-- Controls row: search + toggles (mobile-first) -->
            <div class="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">

              <!-- Toggle: only my cards -->
                <button
                  role="switch"
                  :aria-checked="onlyMine"
                  @click="onlyMine = !onlyMine"
                  class="switch"
                >
                  <span class="switch-knob" :class="{ 'switch-on': onlyMine }"></span>
                  <span class="switch-label">Show only my cards</span>
                </button>

              <!-- Toggle: hide older done -->
              <div class="sm:col-span-1">
                <button
                  role="switch"
                  :aria-checked="hideOlderDone"
                  @click="hideOlderDone = !hideOlderDone"
                  class="switch"
                >
                  <span class="switch-knob" :class="{ 'switch-on': hideOlderDone }"></span>
                  <span class="switch-label">Hide older Done (&gt; 7 days)</span>
                </button>
              </div>
              <!-- Search -->
              <div class="sm:col-span-1">
                <div class="relative">
                  <input
                    v-model.trim="searchQuery"
                    type="text"
                    placeholder="Search title or description..."
                    class="w-full pl-10 pr-3 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                  />
                  <svg class="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M21 21l-4.35-4.35M10 18a8 8 0 100-16 8 8 0 000 16z"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <!-- Board -->
          <div class="flex flex-col sm:flex-row sm:gap-6 space-y-6 sm:space-y-0 sm:overflow-x-autopb-6 px-2">
            <div
              v-for="list in filteredBoard.lists"
              :key="list.id"
              :data-list-id="list.id"
              class="list-container flex-shrink-0 w-80 mb-5"
              :class="{ 'list-drag-over': dragOverList === list.id }"
              @dragover.prevent="handleListDragOver($event, list.id)"
              @drop.prevent="handleDrop($event, list.id)"
              @dragleave="handleListDragLeave"
            >
              <div class="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden mb-5">
                <!-- List Header -->
                <div class="list-header p-4 bg-gradient-to-r" :class="list.color">
                  <div class="flex items-center justify-between">
                    <h3 class="font-bold text-white text-lg">{{ list.title }}</h3>
                    <span class="text-white/80 bg-white/20 px-3 py-1 rounded-full text-sm font-medium">
                      {{ list.cards.length }}
                    </span>
                  </div>
                </div>

                <!-- Cards -->
                <div class="p-4 space-y-3 min-h-[200px]">
                  <div
                    v-for="card in list.cards"
                    :key="card.id"
                    class="card bg-white rounded-xl p-4 shadow-sm border border-gray-100 cursor-pointer group"
                    :class="{ dragging: draggedCard === card.id, stale: isStale(card, list) }"
                    draggable="true"
                    @dragstart="handleDragStart($event, card.id, list.id)"
                    @dragend="handleDragEnd"
                    @click="openModal(list.id, card)"
                    @touchstart.passive="onTouchStart($event, card.id, list.id)"
                    @touchmove.prevent="onTouchMove"
                    @touchend="onTouchEnd"
                  >
                  <small>{{ formatTimestamp(card.createdAt) }}</small>
                  <span
                    class="text-xs font-medium ml-2"
                    :class="isUnder24h(card.createdAt) ? 'text-green-600' : 'text-red-600'"
                  >
                    <template v-if="timerState(card.createdAt).mode === 'down'">
                      {{ msToHMS(timerState(card.createdAt).ms) }} left
                    </template>
                    <template v-else>
                      +{{ msToHMS(timerState(card.createdAt).ms) }}
                    </template>
                  </span>


                    <div class="flex items-start justify-between mb-3">
                      <h4 class="font-semibold text-gray-900 group-hover:text-blue-600 transition flex-1 pr-2">
                        {{ card.title }}
                      </h4>
                      <button
                        @click.stop="openModal(list.id, card)"
                        class="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-500 p-1 rounded transition"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 
                               002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 
                               112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                      </button>
                    </div>

                    <p v-if="card.description" class="text-sm text-gray-600 mb-3 line-clamp-2">
                      {{ card.description }}
                    </p>

                    <div v-if="card.labels.length" class="flex flex-wrap gap-1 mb-3">
                      <span
                        v-for="label in card.labels"
                        :key="label"
                        class="text-xs px-3 py-1 rounded-full font-medium border"
                        :class="labelColors[label] || 'bg-gray-100 text-gray-800 border-gray-200'"
                      >
                        {{ label }}
                      </span>
                    </div>

                    <div class="flex items-center justify-between">
                      <div class="flex -space-x-2">
                        <div
                          v-for="userId in card.assignedUsers.slice(0, 3)"
                          :key="userId"
                          class="w-8 h-8 rounded-full border-2 border-white overflow-hidden shadow-sm"
                        >
                          <div
                            class="w-7 h-7 rounded-full flex items-center justify-center text-black text-sm font-bold"
                            :style="{ backgroundColor: getAvatarColor(userId) }"
                            :title="$store.getters['leads/usernameById'](userId)"
                          >
                            {{ getInitials($store.getters['leads/usernameById'](userId)) }}
                          </div>
                        </div>
                        <div
                          v-if="card.assignedUsers.length > 3"
                          class="w-8 h-8 rounded-full bg-gray-200 border-2 border-white flex items-center justify-center text-xs text-gray-600 font-medium shadow-sm"
                        >
                          +{{ card.assignedUsers.length - 3 }}
                        </div>
                      </div>

                      <div class="flex items-center gap-3 text-gray-400">
                        <div v-if="card.shippingLabels && card.shippingLabels.length" class="flex items-center gap-1" title="Shipping Labels">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
                          </svg>
                          <span class="text-xs font-medium">{{ card.shippingLabels.length }}</span>
                        </div>
                        <div v-if="card.comments.length" class="flex items-center gap-1">
                          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M8 12h.01M12 12h.01M16 12h.01M21 
                                 12c0 4.418-4.03 8-9 8a9.863 9.863 
                                 0 01-4.255-.949L3 20l1.395-3.72C3.512 
                                 15.042 3 13.574 3 12c0-4.418 4.03-8 
                                 9-8s9 3.582 9 8z"/>
                          </svg>
                          <span class="text-xs font-medium">{{ card.comments.length }}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Add Card Button -->
                  <button
                    @click="openModal(list.id)"
                    class="add-card-button w-full p-4 text-gray-500 border-2 border-dashed border-gray-200 rounded-xl transition flex items-center justify-center gap-2"
                  >
                    <svg class="w-5 h-5 group-hover:scale-110 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
                    </svg>
                    <span class="font-medium">Add a task</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Unified Create/Edit Modal (unchanged except it now shows Created By if present) -->
        <div
          v-if="modalVisible"
          class="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4 z-50"
          @click.self="closeModal"
        >
          <div class="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl animate-fade-in">
            <!-- Header -->
            <div class="flex items-center justify-between p-6 border-b border-gray-100">
              <div class="flex items-center gap-3 flex-1">
                <div class="p-2 bg-blue-50 rounded-lg">
                  <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 
                         002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 
                         0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                </div>
                <input
                  v-model="form.title"
                  placeholder="Card title..."
                  class="text-2xl font-bold bg-transparent border-none outline-none flex-1 text-gray-900"
                />
              </div>

              <div class="hidden sm:flex items-center gap-2 mr-4">
                <button
                  @click="activeSection = 'details'"
                  :class="[
                    'px-3 py-1 rounded-full text-sm font-medium',
                    activeSection === 'details'
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600'
                  ]"
                >
                  Details
                </button>
                <button
                  @click="activeSection = 'shipping'"
                  :disabled="isLegacyCard"
                  :title="isLegacyCard ? 'Not available for cards created before Dec 3rd' : ''"
                  :class="[
                    'px-3 py-1 rounded-full text-sm font-medium',
                    activeSection === 'shipping'
                      ? 'bg-gray-900 text-white'
                      : isLegacyCard
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-100 text-gray-600'
                  ]"
                >
                  Shipping & Labels
                </button>
              </div>

              <button @click="closeModal" class="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>

            <div class="p-6 space-y-4 overflow-hidden relative">
              <div
                v-if="activeSection === 'details'"
                class="transition-transform duration-200"
              >
                <div v-if="form.createdBy">
                  <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    Created By
                    <div
                      class="w-8 h-8 rounded-full flex items-center justify-center  text-sm font-bold"
                      :style="{ backgroundColor: getAvatarColor(form.createdBy) }"
                    >
                      {{ getInitials(getUserById(form.createdBy).name) }}
                    </div>
                    {{ getUserById(form.createdBy).name }}
                  </label>
                </div>

                <div>
                  <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7"/>
                    </svg>
                    Description
                  </label>
                  <textarea
                    v-model="form.description"
                    rows="4"
                    class="w-full p-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 "
                    placeholder="Add a detailed description..."
                  ></textarea>
                </div>

                <!-- hide due date input (kept) -->
                <div style="display: none;">
                  <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 
                          002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 
                          2 0 002 2z"/>
                    </svg>
                    Due Date
                  </label>
                  <input
                    v-model="form.dueDate"
                    type="date"
                    class="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div v-if="form.labels.length">
                  <label class="block text-sm font-semibold text-gray-700 mb-2">Selected Labels</label>
                  <div class="flex flex-wrap gap-2 mb-4">
                    <span
                      v-for="lab in form.labels"
                      :key="lab"
                      class="flex items-center gap-1 px-3 py-1 rounded-full border font-medium"
                      :class="labelColors[lab] || 'bg-gray-100 text-gray-800 border-gray-200'"
                    >
                      {{ lab }}
                      <button @click="toggleLabel(lab)" class="p-1 hover:bg-black/10 rounded-full">
                        <svg class="w-3 h-3 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                      </button>
                    </span>
                  </div>
                </div>
                  <!-- Labels Picker -->
                  <div>
                    <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M7 7h.01M7 3h5c.512 0 1.024.195 
                            1.414.586l7 7a2 2 0 010 2.828l-7 
                            7a2 2 0 01-2.828 0l-7-7A1.994 
                            1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      Labels
                    </label>
                    <div class="flex flex-wrap gap-2 mb-3">
                      <button
                        v-for="lab in availableLabels"
                        :key="lab"
                        @click="toggleLabel(lab)"
                        :class="[
                          'px-3 py-2 rounded-full border font-medium transition-all',
                          form.labels.includes(lab)
                            ? 'bg-gray-200 text-gray-600 border-gray-300'
                            : labelColors[lab]
                        ]"
                      >{{ lab }}</button>
                    </div>
                  </div>

                  <div v-if="form.labels.includes('Order') || form.labels.includes('Sample')" class="mt-4">
                  <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M3 7h18M3 12h18M3 17h18" />
                    </svg>
                    Shipping Address
                  </label>

                  <div class="space-y-3">
                    <input
                      v-model="form.shipping.name"
                      type="text"
                      placeholder="Recipient Name"
                      class="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      v-model="form.shipping.line1"
                      type="text"
                      placeholder="Street address"
                      class="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      v-model="form.shipping.line2"
                      type="text"
                      placeholder="Apt, suite, etc. (optional)"
                      class="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />

                    <div class="grid grid-cols-3 gap-3">
                      <input
                        v-model="form.shipping.city"
                        type="text"
                        placeholder="City"
                        class="col-span-1 p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        v-model="form.shipping.state"
                        type="text"
                        placeholder="State"
                        maxlength="2"
                        @input="validateState($event)"
                        class="col-span-1 p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <input
                        v-model="form.shipping.zip"
                        type="text"
                        placeholder="ZIP"
                        class="col-span-1 p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>


                  <!-- ORDER: Items picker (only when "Order" label applied) -->
                  <div v-if="form.labels.includes('Order')" class="mt-4">
                    <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M4 6h16M4 12h16M4 18h7"/>
                      </svg>
                      Order Items
                    </label>

                    <div class="space-y-2">
                      <div
                        v-for="(line, i) in orderLines"
                        :key="line.uid"
                        class="grid grid-cols-12 gap-2 items-center"
                      >
                        <div class="col-span-9">
                          <select v-model="line.sku" class="w-full border rounded px-2 py-2">
                            <option value="">Select SKU</option>
                            <option
                              v-for="opt in skuOptions"
                              :key="opt.sku"
                              :value="opt.sku"
                            >
                              {{ opt.sku }} — {{ opt.name }} (avail: {{ opt.totalQty }})
                            </option>
                          </select>
                        </div>

                        <div class="col-span-2">
                          <input
                            v-model.number="line.qty"
                            type="number"
                            min="1"
                            class="w-full border rounded px-2 py-2"
                            placeholder="Qty"
                          />
                        </div>

                        <div class="col-span-1 text-right">
                          <button type="button" @click="removeOrderLine(i)" class="text-red-600 text-sm">
                            Remove
                          </button>
                        </div>

                        <div class="col-span-12 text-xs text-red-600" v-if="lineError(line)">
                          {{ lineError(line) }}
                        </div>
                      </div>

                      <button type="button" @click="addOrderLine" class="text-sm px-3 py-1 border rounded">
                        + Add Item
                      </button>
                    </div>
                  </div>

                <div>
                  <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <svg style="width: 20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13 20V18C13 15.2386 10.7614 13 8 13C5.23858 13 3 15.2386 3 18V20H13ZM13 20H21V19C21 16.0545 18.7614 14 16 14C14.5867 14 13.3103 14.6255 12.4009 15.6311M11 7C11 8.65685 9.65685 10 8 10C6.34315 10 5 8.65685 5 7C5 5.34315 6.34315 4 8 4C9.65685 4 11 5.34315 11 7ZM18 9C18 10.1046 17.1046 11 16 11C14.8954 11 14 10.1046 14 9C14 7.89543 14.8954 7 16 7C17.1046 7 18 7.89543 18 9Z" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                    Assigned Users
                  </label>
                  <div class="flex flex-wrap gap-3">
                    <button
                      v-for="user in users.filter(u => u.is_active === 1)"
                      :key="user.id"
                      @click="toggleUser(user.id)"
                      :class="[
                        'flex items-center gap-3 px-4 py-2 rounded-xl border transition',
                        form.assignedUsers.includes(user.id)
                          ? 'bg-blue-50 border-blue-200 text-blue-800'
                          : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100'
                      ]"
                    >
                      <div
                        class="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                        :style="{ backgroundColor: getAvatarColor(user.id) }"
                      >
                        {{ getInitials(user.name) }}
                      </div>
                      <span class="font-medium">{{ user.name }}</span>
                    </button>
                  </div>
                </div>

                <div v-if="isEditing">
                  <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 12h.01M12 12h.01M16 12h.01M21 
                          12c0 4.418-4.03 8-9 8a9.863 9.863 
                          0 01-4.255-.949L3 20l1.395-3.72C3.512 
                          15.042 3 13.574 3 12c0-4.418 4.03-8 
                          9-8s9 3.582 9 8z"/>
                    </svg>
                    Comments
                  </label>

                  <div class="flex gap-4 p-4 bg-gray-50 rounded-xl mb-4">
                    <div
                      class="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-md"
                      :style="{ backgroundColor: getAvatarColor(currentUser.id) }"
                    >
                      {{ getInitials(currentUser.name) }}
                    </div>
                    <div class="flex-1 flex items-end flex-col">
                      <textarea
                        v-model="newComment"
                        rows="3"
                        class="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                        placeholder="Write a comment..."
                      ></textarea>
                      <button @click="addComment" class="btn-primary mt-2 px-4 py-2 rounded-lg">
                        Add Comment
                      </button>
                    </div>
                  </div>

                  <div class="space-y-4">
                    <div
                      v-for="c in form.comments"
                      :key="c.id"
                      class="flex gap-4 animate-fade-in"
                    >
                      <div
                        class="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-md"
                        :style="{ backgroundColor: getAvatarColor(c.userId) }"
                      >
                        {{ getInitials(getUserById(c.userId).name) }}
                      </div>
                      <div class="flex-1">
                        <div class="bg-gray-50 rounded-xl p-4 border border-gray-100">
                          <div class="flex items-center gap-3 mb-2">
                            <span class="font-semibold text-gray-900">{{ getUserById(c.userId).name }}</span>
                            <span class="text-sm text-gray-500">{{ formatTimestamp(c.timestamp) }}</span>
                          </div>
                          <p class="text-gray-700">{{ c.text }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div
                v-else
                class="transition-transform duration-200"
              >
                <div class="space-y-3 mb-6">
                  <label class="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M3 7h18M3 12h18M3 17h18" />
                    </svg>
                    Shipping Address
                  </label>

                  <input
                    v-model="form.shipping.name"
                    type="text"
                    placeholder="Recipient Name"
                    class="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    v-model="form.shipping.line1"
                    type="text"
                    placeholder="Street address"
                    class="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <input
                    v-model="form.shipping.line2"
                    type="text"
                    placeholder="Apt, suite, etc. (optional)"
                    class="w-full p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />

                  <div class="grid grid-cols-3 gap-3">
                    <input
                      v-model="form.shipping.city"
                      type="text"
                      placeholder="City"
                      class="col-span-1 p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      v-model="form.shipping.state"
                      type="text"
                      placeholder="State"
                      maxlength="2"
                      @input="validateState($event)"
                      class="col-span-1 p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <input
                      v-model="form.shipping.zip"
                      type="text"
                      placeholder="ZIP"
                      class="col-span-1 p-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <!-- Order items block: reuse your existing Order Items picker here -->

                <!-- Shipping labels -->
                <div class="mt-6">
                  <div class="flex items-center justify-between mb-3">
                    <label class="flex items-center gap-2 text-sm font-semibold text-gray-700">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M3 7h18M3 12h18M3 17h18" />
                      </svg>
                      Shipping Labels
                    </label>


                  </div>

                  <!-- Parcel Selection -->
                  <div class="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Parcel Configuration</label>
                    <div class="flex gap-2 mb-3">
                      <button
                        type="button"
                        @click="parcelMode = 'suggested'"
                        :class="['px-3 py-1.5 text-sm font-medium rounded-md border', parcelMode === 'suggested' ? 'bg-white border-blue-500 text-blue-600 shadow-sm' : 'bg-transparent border-transparent text-gray-500 hover:text-gray-700']"
                      >
                        Suggested
                      </button>
                      <button
                        type="button"
                        @click="parcelMode = 'predefined'"
                        :class="['px-3 py-1.5 text-sm font-medium rounded-md border', parcelMode === 'predefined' ? 'bg-white border-blue-500 text-blue-600 shadow-sm' : 'bg-transparent border-transparent text-gray-500 hover:text-gray-700']"
                      >
                        Predefined
                      </button>
                      <button
                        type="button"
                        @click="parcelMode = 'custom'"
                        :class="['px-3 py-1.5 text-sm font-medium rounded-md border', parcelMode === 'custom' ? 'bg-white border-blue-500 text-blue-600 shadow-sm' : 'bg-transparent border-transparent text-gray-500 hover:text-gray-700']"
                      >
                        Custom
                      </button>
                    </div>

                    <div v-if="parcelMode === 'predefined'">
                      <select v-model="selectedParcelId" class="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                        <option :value="null">Select a parcel...</option>
                        <option v-for="p in parcels" :key="p.id" :value="p.id">
                          {{ p.name }} ({{ p.length }}x{{ p.width }}x{{ p.height }})
                        </option>
                      </select>
                    </div>

                    <div v-if="parcelMode === 'custom'" class="grid grid-cols-3 gap-2">
                      <div>
                        <label class="block text-xs text-gray-500 mb-1">Length</label>
                        <input v-model.number="customDimensions.length" type="number" class="w-full p-2 border border-gray-300 rounded-lg text-sm" placeholder="L">
                      </div>
                      <div>
                        <label class="block text-xs text-gray-500 mb-1">Width</label>
                        <input v-model.number="customDimensions.width" type="number" class="w-full p-2 border border-gray-300 rounded-lg text-sm" placeholder="W">
                      </div>
                      <div>
                        <label class="block text-xs text-gray-500 mb-1">Height</label>
                        <input v-model.number="customDimensions.height" type="number" class="w-full p-2 border border-gray-300 rounded-lg text-sm" placeholder="H">
                      </div>
                    </div>

                    <div class="mt-3">
                      <label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Insurance Amount ($)</label>
                      <input 
                        v-model.number="insuranceAmount" 
                        type="number" 
                        min="0" 
                        step="0.01"
                        class="w-full p-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      >
                    </div>
                  </div>

                  <div class="flex justify-end mb-4">
                    <button
                      type="button"
                      class="px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 hover:bg-gray-50 flex items-center gap-2"
                      :disabled="fetchingRates || (!form.orderItems.length && !form.labels.includes('Sample'))"
                      @click="fetchRates"
                    >
                      <svg v-if="fetchingRates" class="animate-spin h-4 w-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{{ fetchingRates ? 'Fetching...' : 'Get Rates' }}</span>
                    </button>
                  </div>

                  <div v-if="availableRates.length" class="mb-4 space-y-2">
                    <div v-if="suggestedBox" class="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex items-center gap-3">
                      <div class="p-2 bg-blue-100 rounded-full text-blue-600">
                        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
                        </svg>
                      </div>
                      <div>
                        <div class="text-xs font-semibold text-blue-800 uppercase tracking-wide">Suggested Box</div>
                        <div class="text-sm font-medium text-blue-900">
                          {{ suggestedBox.name }}
                          <span class="text-blue-700 font-normal">({{ suggestedBox.length }}x{{ suggestedBox.width }}x{{ suggestedBox.height }})</span>
                        </div>
                      </div>
                    </div>

                    <label class="block text-sm font-semibold text-gray-700 mb-2">Select Shipping Rate</label>
                    <div 
                      v-for="rate in availableRates" 
                      :key="rate.rateId"
                      class="flex items-center justify-between p-3 border rounded-xl cursor-pointer transition-colors"
                      :class="selectedRate?.rateId === rate.rateId ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:bg-gray-50'"
                      @click="selectedRate = rate"
                    >
                      <div class="flex items-center gap-3">
                        <div class="w-4 h-4 rounded-full border border-gray-300 flex items-center justify-center"
                             :class="selectedRate?.rateId === rate.rateId ? 'border-green-500' : ''">
                          <div v-if="selectedRate?.rateId === rate.rateId" class="w-2 h-2 rounded-full bg-green-500"></div>
                        </div>
                        <div>
                          <div class="font-medium text-gray-900">{{ rate.serviceName }}</div>
                          <div v-if="rate.deliveryDays" class="text-xs text-gray-500">{{ rate.deliveryDays }} day(s)</div>
                        </div>
                      </div>
                      <div class="font-bold text-gray-900">${{ rate.shipmentCost.toFixed(2) }}</div>
                    </div>
                  </div>

                  <div v-if="availableRates.length" class="flex justify-end mt-4 mb-6">
                    <button
                      type="button"
                      class="btn-primary px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-60"
                      :disabled="purchasingLabel || !selectedRate"
                      @click="purchaseLabelForCard"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                              d="M12 4v16m8-8H4"/>
                      </svg>
                      <span>{{ purchasingLabel ? 'Purchasing...' : 'Purchase Label' }}</span>
                    </button>
                  </div>

                  <p class="text-xs text-gray-500 mb-3">
                    Uses current order items and shipping address to create a label.
                  </p>

                  <div v-if="form.shippingLabels.length" class="space-y-3">
                    <div
                      v-for="lab in form.shippingLabels"
                      :key="lab.id"
                      class="flex items-center justify-between p-3 border rounded-xl"
                      :class="lab.voided ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'"
                    >
                      <div>
                        <div class="text-sm font-semibold text-gray-800">
                          {{ lab.carrier }} {{ lab.service || '' }}
                        </div>
                        <div class="text-xs text-gray-600">
                          Tracking: {{ lab.trackingNumber }}
                        </div>
                        <div class="text-xs text-gray-400">
                          {{ formatTimestamp(lab.createdAt || lab.created_at) }}
                        </div>
                        <div v-if="lab.voided" class="text-xs font-bold text-red-600 mt-1 flex items-center gap-1">
                          <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                          </svg>
                          LABEL VOIDED
                        </div>
                      </div>
                      <div class="flex flex-col gap-2 items-end">
                        <button
                          type="button"
                          class="text-xs px-3 py-1 border rounded-lg hover:bg-gray-100"
                          @click="copyTracking(lab.trackingNumber)"
                        >
                          Copy tracking
                        </button>
                        <a
                          v-if="lab.labelUrl"
                          :href="lab.labelUrl"
                          target="_blank"
                          rel="noopener"
                          class="text-xs px-3 py-1 border rounded-lg hover:bg-gray-100 text-blue-700"
                        >
                          Open label
                        </a>
                        <button
                          v-if="!lab.voided"
                          type="button"
                          class="text-xs px-3 py-1 border rounded-lg hover:bg-red-50 text-red-600 border-red-200"
                          @click="voidLabelForCard(lab)"
                        >
                          Void
                        </button>
                        <span v-else class="text-xs px-3 py-1 bg-red-100 text-red-800 rounded-lg font-medium">
                          Voided
                        </span>
                      </div>
                    </div>
                  </div>

                  <p v-else class="text-xs text-gray-500 italic">
                    No labels generated yet.
                  </p>
                </div>

              </div>
            </div>

            <div class="flex justify-end gap-3 p-6 border-t border-gray-100 bg-gray-50 rounded-b-2xl">
              <button
                v-if="isEditing"
                @click="confirmDelete()"
                class="px-6 py-2 text-red-600 hover:bg-red-50 rounded-lg font-medium"
              >
                Delete Card
              </button>
              <button @click="closeModal" class="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium">
                Cancel
              </button>
              <button @click="saveCard" class="btn-primary px-6 py-2 text-white rounded-lg font-medium">
                {{ isEditing ? 'Save Changes' : 'Create Card' }}
              </button>
            </div>
          </div>
        </div>
        <!-- end modal -->

        <!-- Notifications drawer (unchanged) -->
        <div
          class="fixed top-0 right-0 h-full w-100 bg-white shadow-xl transform transition-transform z-50"
          :class="drawerOpen ? 'translate-x-0' : 'translate-x-full'"
          @click.self="drawerOpen = false"
        >
          <div class="flex items-center justify-between p-4 border-b">
            <h2 class="text-lg font-semibold">Notifications</h2>
            <button @click="drawerOpen = false" class="text-gray-500 hover:text-gray-700">
              <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>

          <ul class="divide-y overflow-y-auto h-full">
            <li
              v-for="note in notifications"
              :key="note.id"
              :class="['flex items-start p-4 space-x-3', !note.is_read ? 'unread-div' : '']"
              class="flex items-start p-4 space-x-3 items-center"
            >
              <div class="flex-shrink-0 text-gray-400 notification-icon-div">
                <template v-if="note.type === 'comment'">
                  <svg style="width: 20px" sfill="#000000" viewBox="0 0 1920 1920" xmlns="http://www.w3.org/2000/svg"><path d="M1662.178 0v1359.964h-648.703l-560.154 560.154v-560.154H0V0h1662.178ZM1511.07 151.107H151.107v1057.75h453.321v346.488l346.489-346.488h560.154V151.107ZM906.794 755.55v117.53H453.32V755.55h453.473Zm302.063-302.365v117.529H453.32V453.185h755.536Z" fill-rule="evenodd"></path></svg>
                </template>
                <template v-else-if="note.type === 'move'">
                  <svg style="width: 20px" fill="#000000" viewBox="0 0 452.025 452.025" xml:space="preserve"><g><g><path d="M147.912,363.325c-4.7-4.7-12.3-4.7-17,0c-4.7,4.7-4.7,12.3,0,17l13.6,13.6h-55.2c-35.9,0-65-29.2-65-65v-40.3 c0-6.6-5.4-12-12-12s-12,5.4-12,12v40.3c0,49.1,39.9,89,89,89h55.2l-13.6,13.6c-4.7,4.7-4.7,12.3,0,17c2.3,2.3,5.4,3.5,8.5,3.5 s6.1-1.2,8.5-3.5l34.1-34.1c4.7-4.7,4.7-12.3,0-17L147.912,363.325z"></path><path d="M210.312,0.025h-197.1c-6.6,0-12,5.4-12,12v197.1c0,6.6,5.4,12,12,12h197.1c6.6,0,12-5.4,12-12v-197.1 C222.312,5.425,217.012,0.025,210.312,0.025z M198.312,197.125h-173.1v-173.1h173.1V197.125z"></path><path d="M362.612,34.125h-55.2l13.6-13.6c4.7-4.7,4.7-12.3,0-17s-12.3-4.7-17,0l-34,34.1c-4.7,4.7-4.7,12.3,0,17l34.1,34.1 c2.3,2.3,5.4,3.5,8.5,3.5s6.1-1.2,8.5-3.5c4.7-4.7,4.7-12.3,0-17l-13.6-13.6h55.2c35.9,0,65,29.2,65,65v40.3c0,6.6,5.4,12,12,12 s12-5.4,12-12v-40.3C451.712,74.025,411.712,34.125,362.612,34.125z"></path></g></g></svg>
                </template>
                <template v-else-if="note.type === 'assignment'">
                  <svg style="width: 20px" fill="#000000" viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M18,17a7.46,7.46,0,1,0-7.45-7.46A7.46,7.46,0,0,0,18,17ZM18,4.07a5.46,5.46,0,1,1-5.45,5.45A5.46,5.46,0,0,1,18,4.07Z"></path><path d="M6,31.89V25.77a16.13,16.13,0,0,1,12-5,16.61,16.61,0,0,1,8.71,2.33l1.35-1.51A18.53,18.53,0,0,0,18,18.74,17.7,17.7,0,0,0,4.21,24.8a1,1,0,0,0-.21.6v6.49A2.06,2.06,0,0,0,6,34H18.39l-1.9-2Z"></path><path d="M30,31.89,30,32H26.85l-1.8,2H30a2.06,2.06,0,0,0,2-2.07V26.2l-2,2.23Z"></path><path d="M34.76,18.62a1,1,0,0,0-1.41.08l-11.62,13-5.2-5.59A1,1,0,0,0,15.12,26a1,1,0,0,0-.06,1.42l6.69,7.2L34.84,20A1,1,0,0,0,34.76,18.62Z"></path></svg>
                </template>
                <template v-else>
                  <svg style="width: 20px" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg" fill="#000000"><g fill-rule="evenodd"><path d="M0 7a7 7 0 1 1 14 0A7 7 0 0 1 0 7z"></path><path d="M13 7A6 6 0 1 0 1 7a6 6 0 0 0 12 0z" fill="#FFF"></path><path d="M6 3.5c0-.3.2-.5.5-.5h1c.3 0 .5.2.5.5v4c0 .3-.2.5-.5.5h-1c-.3 0-.5-.2-.5-.5v-4m0 6c0-.3.2-.5.5-.5h1c.3 0 .5.2.5.5v1c0 .3-.2.5-.5.5h-1c-.3 0-.5-.2-.5-.5v-1"></path></g></svg>
                </template>
              </div>

              <div class="flex-1">
                <p class="text-sm text-gray-800 mb-1 font-bold">{{ note.message }}</p>
                <span class="text-xs text-gray-500">{{ formatTimestamp(note.created_at) }}</span>
              </div>

              <button
                v-if="!note.is_read"
                @click="markRead(note)"
                class="flex-shrink-0 text-green-600 hover:text-green-800"
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
                </svg>
              </button>
            </li>

            <li v-if="!notifications.length" class="p-4 text-center text-gray-500">
              No notifications
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch } from 'vue'
import { useStore } from 'vuex'
import SideBar from '@/components/SideBar.vue'
import Swal from 'sweetalert2'
import { fetchBoard, editCard, deleteCard, addComment as addCommentApi, fetchNotifications, markNotificationRead } from '@/api/kanban'
import { purchaseLabel as purchaseLabelApi, getRates as getRatesApi, fetchProducts, fetchParcels, voidLabel, fetchSenderInfo } from '@/api/labels'

const store = useStore()

// State
const board = reactive({ lists: [] })
const draggedCard = ref(null)
const draggedFromList = ref(null)
const dragOverList = ref(null)
const notifications = ref([])
const drawerOpen = ref(false)
const touchCard = ref(null)
const touchFromList = ref(null)

const activeSection = ref('details') // 'details' | 'shipping'
const purchasingLabel = ref(false)
const fetchingRates = ref(false)
const availableRates = ref([])
const selectedRate = ref(null)
const senderInfo = ref(null)
const suggestedBox = ref(null)

// Parcel selection
const parcels = ref([])
const parcelMode = ref('suggested') // 'suggested' | 'predefined' | 'custom'
const selectedParcelId = ref(null)
const customDimensions = reactive({ length: '', width: '', height: '' })
const insuranceAmount = ref(0)


// NEW: filters
const searchQuery = ref('')
const onlyMine = ref(true)
const hideOlderDone = ref(true)


const now = ref(Date.now())
let tick;

// unified modal state
const modalVisible = ref(false)
const form = reactive({
  id: null,
  listId: null,
  title: '',
  description: '',
  dueDate: '',
  labels: [],
  assignedUsers: [],
  comments: [],
  createdBy: null,
  orderItems: [],
  shipping: {
    name: '',
    line1: '',
    line2: '',
    city: '',
    state: '',
    zip: '',
  }, 
  shippingLabels: [], 
  createdAt: null,
})
const newComment = ref('')

// computed
const labelColors = {
  Order: 'bg-orange-100 text-orange-800 border-orange-200',
  Sample: 'bg-purple-100 text-purple-800 border-purple-200',
  General: 'bg-blue-100 text-blue-800 border-blue-200',
  Urgent: 'bg-red-100 text-red-800 border-red-200'
}

const users = computed(() => store.getters['leads/allUsers'])
const currentUser = computed(() => store.getters['auth/currentUser'])
const availableLabels = computed(() => Object.keys(labelColors))
const isEditing = computed(() => !!form.id)
const unreadCount = computed(() => notifications.value.filter(n => !n.is_read).length)
const isLegacyCard = computed(() => {
  if (!form.createdAt) return false // New cards are not legacy
  // Check if created before Dec 3rd, 2025
  return new Date(form.createdAt) < new Date('2025-12-03T00:00:00')
})

// Hardcoded sample product for Sample shipments (1oz, 4x4x6 inches)
const SAMPLE_PRODUCT = {
  sku: 'SAMPLE',
  name: 'Sample Product',
  qty: 1,
  weight: 1, // 1 oz
  length: 6,
  width: 4,
  height: 4,
}

const orderLines = ref([{ uid: 1, sku: '', qty: null }])
let orderUid = 2

const products = ref([])

// show ALL predefined SKUs; merge live totals from inventory summary (defaults to 0)
const skuOptions = computed(() => {
  let summary = store.getters['inventory/summary']
  if (!Array.isArray(summary) && summary && Array.isArray(summary.summary)) {
    summary = summary.summary
  }
  if (!Array.isArray(summary)) summary = []

  const bySku = new Map(summary.map(s => [s.sku, s]))
  return products.value.map(item => ({
    sku: item.sku,
    name: item.name,
    totalQty: Number(bySku.get(item.sku)?.totalQty ?? 0) // 0 if not in summary
  }))
})


const productBySku = computed(() => {
  const map = new Map()
  ;(products.value || []).forEach(p => map.set(p.sku, p))
  return map
})

function addOrderLine() {
  orderLines.value.push({ uid: orderUid++, sku: '', qty: null })
}
function removeOrderLine(i) {
  orderLines.value.splice(i, 1)
}

function lineError(line) {
  if (!line.sku && (line.qty ?? 0) > 0) return 'Select a SKU.'
  if (line.sku && (!line.qty || line.qty < 1)) return 'Quantity must be at least 1.'
  return ''
}

function validateOrderLines() {
  // only validate when "Order" label selected
  if (!form.labels.includes('Order')) return true
  const filled = orderLines.value.filter(l => l.sku && Number(l.qty) > 0)
  if (!filled.length) return 'Add at least one item with a quantity.'
  const bad = orderLines.value.find(l => lineError(l))
  return bad ? lineError(bad) : true
}

// helpers
const getUserById = id => users.value.find(u => u.id === id) || {}
const getAvatarColor = id => {
  const palette = ['#FFB3BA','#FFDFBA','#FFFFBA','#BAFFC9','#BAE1FF','#E5BAFF','#D5F4E6','#FFCCE5','#C5FAD5','#FFF2CC']
  return palette[id % palette.length]
}
const getInitials = name => (name || '').split(' ').map(p => p[0]).slice(0,2).join('').toUpperCase()
const formatTimestamp = ts => new Date(ts).toLocaleString('en-US', { month:'short', day:'numeric', hour:'numeric', minute:'2-digit' })

function getCardTimestamp(card) {
  return card?.createdAt || card?.updatedAt || null
}

function isOlderThan7Days(dateLike) {
  console.log(dateLike)
  if (!dateLike) return false
  const dt = new Date(dateLike)
  if (isNaN(+dt)) return false
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000
  return (Date.now() - dt.getTime()) > sevenDaysMs
}

// NEW: core filter
const filteredBoard = computed(() => {
  const q = (searchQuery.value || '').toLowerCase()
  const meId = currentUser.value?.id

  const lists = board.lists.map(l => {
    const cards = (l.cards || []).filter(card => {
      // Hide older Done
      if (hideOlderDone.value && l.title?.toLowerCase().includes('done')) {
        if (isOlderThan7Days(getCardTimestamp(card))) return false
      }

      // Only my cards
      if (onlyMine.value) {
        const mine = (card.assignedUsers || []).includes(meId) || card.createdBy === meId
        if (!mine) return false
      }

      // Search (title/description)
      if (q) {
        const t = (card.title || '').toLowerCase()
        const d = (card.description || '').toLowerCase()
        if (!(t.includes(q) || d.includes(q))) return false
      }

      return true
    })

    return { ...l, cards }
  })

  return { lists }
})

function isStale(card,list) {
  if(list.id != 3 && list.id != 4){
    const created = new Date(card.createdAt).getTime()
    return Date.now() - created > 7 * 24 * 60 * 60 * 1000
  }
   
}

async function markRead(note) {
  await markNotificationRead(note.id)
  note.is_read = true
}

// drag & drop (unchanged)
function handleDragStart(e, cardId, listId) {
  draggedCard.value = cardId
  draggedFromList.value = listId
  e.dataTransfer.effectAllowed = 'move'
}
function handleDragEnd() {
  draggedCard.value = null
  draggedFromList.value = null
  dragOverList.value = null
}
function handleListDragOver(e, listId) {
  if (draggedFromList.value !== listId) dragOverList.value = listId
}
function handleListDragLeave() {
  dragOverList.value = null
}
async function handleDrop(e, targetListId) {
  if (!draggedCard.value || draggedFromList.value === targetListId) return

  const src = board.lists.find(l => l.id === draggedFromList.value)
  const card = src.cards.find(c => c.id === draggedCard.value)
  if (!card) return

  const dest = board.lists.find(l => l.id === targetListId)

  // Restriction: Order cards must have tracking to move to Review or Done
  // Exception: Legacy cards (before Dec 3rd, 2025) are exempt since they can't create shipping labels
  const isReviewOrDone = dest.title.toLowerCase().includes('review') || dest.title.toLowerCase().includes('done')
  const isOrder = card.labels.includes('Order')
  const hasTracking = card.shippingLabels && card.shippingLabels.length > 0
  const isLegacy = card.createdAt && new Date(card.createdAt) < new Date('2025-12-03T00:00:00')

  if (isReviewOrDone && isOrder && !hasTracking && !isLegacy) {
    return Swal.fire({
      icon: 'error',
      title: 'Missing Tracking',
      text: 'Order cards cannot be moved to Review or Done without a shipping label.'
    })
  }

  // Proceed with move
  const idx = src.cards.indexOf(card)
  src.cards.splice(idx, 1)
  dest.cards.push(card)

  try {
    await editCard({
      id: card.id,
      listId: targetListId,
      position: dest.cards.length - 1,
      title: card.title,
      description: card.description,
      dueDate: card.dueDate,
      assignedUsers: card.assignedUsers,
      labels: card.labels,
      orderItems: card.orderItems ?? null
    })
  } catch (err) {
    console.error('❌ Failed to move card on server:', err)
  }

  draggedCard.value = null
  draggedFromList.value = null
}

// modal open/close
function openModal(listId, card = null) {
  if (!card) {
    // NEW CARD
    Object.assign(form, {
      id: null,
      listId,
      title: '',
      description: '',
      dueDate: '',
      labels: [],
      assignedUsers: [],
      comments: [],
      createdBy: currentUser.value?.id || null,
      orderItems: [],
      shipping: {
        name: '',
        line1: '',
        line2: '',
        city: '',
        state: '',
        zip: '',
      },
      shippingLabels: [],
      createdAt: new Date().toISOString(),
    })
    // empty UI lines
    orderLines.value = [{ uid: 1, sku: '', qty: null }]
    orderUid = 2
    availableRates.value = []
    selectedRate.value = null
  } else {
    // EDIT CARD
    form.id = card.id
    form.listId = listId
    form.title = card.title
    form.description = card.description
    form.dueDate = card.dueDate
    form.labels = [...card.labels]
    form.assignedUsers = [...card.assignedUsers]
    form.comments = card.comments.map(c => ({ ...c }))
    form.createdBy = card.createdBy
    form.createdAt = card.createdAt
    form.orderItems = Array.isArray(card.orderItems) ? [...card.orderItems] : []
    form.shipping = {
      name: card.shipping?.name || '',
      line1: card.shipping?.line1 || '',
      line2: card.shipping?.line2 || '',
      city:  card.shipping?.city  || '',
      state: card.shipping?.state || '',
      zip:   card.shipping?.zip   || '',
    };
    form.shippingLabels = Array.isArray(card.shippingLabels)
      ? card.shippingLabels.map(l => ({
          ...l,
          carrier: l.carrier || l.carrierCode,
          service: l.service || l.serviceCode,
          labelUrl: l.labelUrl || l.labelPdfUrl
        }))
      : []

    // List fetch is currently broken/changed endpoint. Commenting out for now.
    /*
    if (form.shippingLabels.length) {
      fetchLabels().then(allLabels => {
        form.shippingLabels.forEach(localLabel => {
          const fresh = allLabels.find(l => l.id === localLabel.id)
          if (fresh) {
            localLabel.voided = fresh.voided
          }
        })
      }).catch(err => console.error('Failed to refresh label status', err))
    }
    */
   
    // Fetch sender info
    fetchSenderInfo().then(info => {
      senderInfo.value = info
    }).catch(err => console.error('Failed to fetch sender info', err))

    availableRates.value = []
    selectedRate.value = null


    const linesFromCard = (form.orderItems || []).map(oi => ({
      uid: orderUid++,
      sku: oi.sku || '',
      qty: Number(oi.qty) || null,
    }))
    orderLines.value = linesFromCard.length
      ? linesFromCard
      : [{ uid: 1, sku: '', qty: null }]
  }

  newComment.value = ''
  modalVisible.value = true
}




function closeModal() {
  modalVisible.value = false
}

// label/user toggles
function toggleLabel(l) {
  const i = form.labels.indexOf(l)
  if (i > -1) form.labels.splice(i,1)
  else form.labels.push(l)
}
function toggleUser(id) {
  const i = form.assignedUsers.indexOf(id)
  if (i > -1) form.assignedUsers.splice(i,1)
  else form.assignedUsers.push(id)
}

// save
async function saveCard() {
  if (!form.title.trim())
    return Swal.fire({ icon: 'error', text: 'Title is required' })

  const hasOrder = form.labels.includes('Order')
  if (hasOrder) {
    const msg = validateOrderLines()
    if (msg !== true) return Swal.fire({ icon: 'error', text: msg })
  }

  // Build orderItems from UI
  const picked = hasOrder
    ? orderLines.value
        .filter(l => l.sku && Number(l.qty) > 0)
        .map(l => {
          const product = productBySku.value.get(l.sku)
          return {
            sku: l.sku,
            name: product?.name || null,
            qty: Math.abs(Number(l.qty)),
            weight: product?.weight || 0,
            width: product?.width || 0,
            height: product?.height || 0,
            length: product?.length || 0,
          }
        })
    : []

  form.orderItems = picked

  const isNew = !form.id   // <-- capture BEFORE saving

  const payload = {
    id: form.id,
    title: form.title,
    description: form.description,
    dueDate: form.dueDate,
    listId: form.listId,
    position: board.lists.find(l => l.id === form.listId).cards.length,
    assignedUsers: form.assignedUsers,
    labels: form.labels,
    orderItems: picked,
    shipping: {
      name: form.shipping.name,
      line1: form.shipping.line1,
      line2: form.shipping.line2,
      city:  form.shipping.city,
      state: form.shipping.state,
      zip:   form.shipping.zip,
    },
    shippingLabels: [...form.shippingLabels],

  }

  try {
    // 1) Save card
    const saved = await editCard(payload)
    const cardId = saved?.card?.id ?? form.id
    if (!cardId) throw new Error('No card id returned from editCard')
    const referenceId = `kanban:${cardId}`

    // 2) Delete old ledger rows ONLY when editing an existing card
    //    (if user removed the Order label, this clears any prior rows)
    if (!isNew) {
      await store.dispatch('inventory/deleteByReference', referenceId)
    }

    // 3) Reinsert current lines only if Order is present now
    if (hasOrder && picked.length) {
      for (const line of picked) {
        await store.dispatch('inventory/createEntry', {
          sku: line.sku,
          name: line.name || null,
          reason: 'sale',
          qtyDelta: -Math.abs(Number(line.qty)),
          referenceId,
        })
      }
      await store.dispatch('inventory/fetchSummary')
    }

    // 4) Refresh board + close
    board.lists.splice(0, board.lists.length, ...(await fetchBoard()))
    closeModal()
  } catch (e) {
    console.error(e)
    Swal.fire({ icon: 'error', text: 'Save failed' })
  }
}


// delete
async function confirmDelete() {
  const result = await Swal.fire({
    title: 'Delete Card?',
    text: form.title,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    confirmButtonText: 'Yes, delete!'
  })
  if (!result.isConfirmed) return

  try {
    await deleteCard(form.id)
    const list = board.lists.find(l => l.id === form.listId)
    list.cards.splice(list.cards.findIndex(c => c.id === form.id), 1)
    closeModal()
  } catch (err) {
    console.error('❌ Delete failed:', err)
    Swal.fire({ icon:'error', text:'Delete failed' })
  }
}

async function addComment() {
  const text = newComment.value.trim()
  if (!text) return
  try {
    const saved = await addCommentApi(form.id, text)
    form.comments.push(saved)
    const list = board.lists.find(l => l.id === form.listId)
    if (list) {
      const card = list.cards.find(c => c.id === form.id)
      if (card) card.comments.push(saved)
    }
    newComment.value = ''
  } catch (err) {
    console.error('❌ addComment error', err)
    Swal.fire({ icon: 'error', text: 'Failed to add comment' })
  }
}

// touch DnD
function onTouchStart(evt, cardId, listId) {
  touchCard.value = cardId
  touchFromList.value = Number(listId)
}
function onTouchMove(evt) {
  const { clientX, clientY } = evt.touches[0]
  const col = document.elementsFromPoint(clientX, clientY).find(el => el.classList.contains('list-container'))
  dragOverList.value = col ? parseInt(col.dataset.listId, 10) : null
}
function onTouchEnd() {
  if (touchCard.value && dragOverList.value && dragOverList.value !== touchFromList.value) {
    draggedCard.value = touchCard.value
    draggedFromList.value = touchFromList.value
    handleDrop({ preventDefault:()=>{}, stopPropagation:()=>{} }, dragOverList.value)
  }
  touchCard.value = null
  touchFromList.value = null
  dragOverList.value = null
}

function msToHMS(ms) {
  const s = Math.max(0, Math.floor(ms / 1000))
  const hh = String(Math.floor(s / 3600)).padStart(2, '0')
  const mm = String(Math.floor((s % 3600) / 60)).padStart(2, '0')
  const ss = String(s % 60).padStart(2, '0')
  return `${hh}:${mm}:${ss}`
}

function timerState(createdAt) {
  const created = typeof createdAt === 'number' ? createdAt : new Date(createdAt).getTime()
  const diff = now.value - created
  const day = 24 * 60 * 60 * 1000
  if (diff < day) {
    return { mode: 'down', ms: day - diff }   // time remaining in green
  }
  return { mode: 'up', ms: diff - day }       // overdue duration in red
}

function isUnder24h(createdAt) {
  const created = typeof createdAt === 'number' ? createdAt : new Date(createdAt).getTime()
  return now.value - created < 24 * 60 * 60 * 1000
}

onMounted(async () => {
  board.lists = await fetchBoard()
  store.dispatch('leads/fetchUserMap')
  notifications.value = await fetchNotifications()
  tick = setInterval(() => { now.value = Date.now() }, 1000) // one shared tick

  try {
    products.value = await fetchProducts()
    parcels.value = await fetchParcels()
  } catch (err) {
    console.error('Failed to fetch products/parcels', err)
  }

  // ORDER: ensure SKU options loaded for picker
  if (!Array.isArray(store.getters['inventory/summary']) || !store.getters['inventory/summary'].length) {
    await store.dispatch('inventory/fetchSummary')
  }
})
onBeforeUnmount(() => clearInterval(tick))

async function fetchRates() {
  // Auto-trim address inputs to handle phantom spaces
  form.shipping.name = (form.shipping.name || '').trim()
  form.shipping.line1 = (form.shipping.line1 || '').trim()
  form.shipping.line2 = (form.shipping.line2 || '').trim()
  form.shipping.city = (form.shipping.city || '').trim()
  form.shipping.state = (form.shipping.state || '').trim()
  form.shipping.zip = (form.shipping.zip || '').trim()

  if (!form.shipping.line1 || !form.shipping.city || !form.shipping.state || !form.shipping.zip) {
    return Swal.fire({ icon: 'error', text: 'Please fill in the full shipping address.' })
  }

  // Validate State/ZIP swap
  const st = form.shipping.state
  const zp = form.shipping.zip
  if (/\d/.test(st)) {
    return Swal.fire({ icon: 'error', text: 'State cannot contain numbers. Did you swap State and ZIP?' })
  }
  if (!/\d/.test(zp)) {
    return Swal.fire({ icon: 'error', text: 'ZIP code must contain numbers. Did you swap State and ZIP?' })
  }

  const isSampleCard = form.labels.includes('Sample')
  const hasOrderItems = form.orderItems.length || orderLines.value.some(l => l.sku && Number(l.qty) > 0)

  // For Order cards, require order items; Sample cards use hardcoded product
  if (!isSampleCard && !hasOrderItems) {
    return Swal.fire({ icon: 'error', text: 'Add at least one order item.' })
  }

  fetchingRates.value = true
  availableRates.value = []
  selectedRate.value = null

  // Use hardcoded sample product for Sample cards, otherwise build from order lines
  let liveItems
  if (isSampleCard) {
    liveItems = [SAMPLE_PRODUCT]
  } else {
    liveItems = orderLines.value
      .filter(l => l.sku && Number(l.qty) > 0)
      .map(l => {
        const product = productBySku.value.get(l.sku)
        return {
          sku: l.sku,
          name: product?.name || null,
          qty: Math.abs(Number(l.qty)),
          weight: product?.weight || 0,
          width: product?.width || 0,
          height: product?.height || 0,
          length: product?.length || 0,
        }
      })
  }

  // Prepare parcel override
  let parcelOverride = null
  if (parcelMode.value === 'predefined' && selectedParcelId.value) {
    const p = parcels.value.find(x => x.id === selectedParcelId.value)
    if (p) {
      parcelOverride = { length: p.length, width: p.width, height: p.height }
    }
  } else if (parcelMode.value === 'custom') {
    if (customDimensions.length && customDimensions.width && customDimensions.height) {
      parcelOverride = { ...customDimensions }
    } else {
       return Swal.fire({ icon: 'error', text: 'Please enter valid dimensions for custom parcel.' })
    }
  }

  try {
    const res = await getRatesApi(form.shipping, liveItems, parcelOverride, insuranceAmount.value)
    // Handle response structure: { rates: [...], box: {...} } or just rates array
    if (res.rates && Array.isArray(res.rates)) {
      availableRates.value = res.rates
      suggestedBox.value = res.selectedBox || null
    } else if (Array.isArray(res)) {
      availableRates.value = res
      suggestedBox.value = null
    } else {
      // Fallback if structure is unexpected
      availableRates.value = []
      suggestedBox.value = null
    }

    if (!availableRates.value.length) {
      Swal.fire({
        icon: 'warning',
        title: 'No Rates Found',
        text: 'Zero shipping rates were returned. Please double-check your address details (especially City, State, and ZIP) and try again.'
      })
    }
  } catch (err) {
    console.error('❌ fetch rates error', err)
    Swal.fire({ icon: 'error', text: 'Failed to fetch rates' })
  } finally {
    fetchingRates.value = false
  }
}

async function purchaseLabelForCard() {
  if (!form.id) {
    return Swal.fire({ icon: 'error', text: 'Save the card before purchasing a label' })
  }

  if (!selectedRate.value) {
    return Swal.fire({ icon: 'error', text: 'Please select a shipping rate.' })
  }

  purchasingLabel.value = true
  try {
    const isSampleCard = form.labels.includes('Sample')

    // Use hardcoded sample product for Sample cards, otherwise build from order lines
    let liveItems
    if (isSampleCard) {
      liveItems = [SAMPLE_PRODUCT]
    } else {
      liveItems = orderLines.value
        .filter(l => l.sku && Number(l.qty) > 0)
        .map(l => {
          const product = productBySku.value.get(l.sku)
          return {
            sku: l.sku,
            name: product?.name || null,
            qty: Math.abs(Number(l.qty)),
            weight: product?.weight || 0,
            width: product?.width || 0,
            height: product?.height || 0,
            length: product?.length || 0,
          }
        })
    }

    // Prepare parcel override
    let parcelOverride = null
    if (parcelMode.value === 'predefined' && selectedParcelId.value) {
      const p = parcels.value.find(x => x.id === selectedParcelId.value)
      if (p) {
        parcelOverride = { length: p.length, width: p.width, height: p.height }
      }
    } else if (parcelMode.value === 'custom') {
      if (customDimensions.length && customDimensions.width && customDimensions.height) {
        parcelOverride = { ...customDimensions }
      }
    }

    const res = await purchaseLabelApi({
      cardId: form.id,
      shipping: form.shipping,
      items: liveItems,
      parcel: parcelOverride,
      rate: selectedRate.value, // Pass selected rate details
      insuranceAmount: insuranceAmount.value
    })

    // assume API returns one label or an array
    const mapLabel = l => ({
      ...l,
      carrier: l.carrier || l.carrierCode,
      service: l.service || l.serviceCode || selectedRate.value?.serviceName,
      labelUrl: l.labelUrl || l.labelPdfUrl,
      createdAt: l.createdAt || l.created_at || new Date().toISOString(),
      voided: l.voided || false
    })

    // assume API returns one label or an array
    const newLabel = res.label || res
    if (Array.isArray(res.labels)) {
      form.shippingLabels.push(...res.labels.map(mapLabel))
    } else if (newLabel) {
      form.shippingLabels.push(mapLabel(newLabel))
    }

    Swal.fire({ icon: 'success', text: 'Label created' })
    // Clear selection after purchase
    availableRates.value = []
    selectedRate.value = null
  } catch (err) {
    console.error('❌ purchase label error', err)
    Swal.fire({ icon: 'error', text: 'Failed to create label' })
  } finally {
    purchasingLabel.value = false
  }
}

async function copyTracking(tracking) {
  if (!tracking) return
  try {
    await navigator.clipboard.writeText(tracking)
    Swal.fire({ icon: 'success', text: 'Tracking number copied' })
  } catch (err) {
    console.error('❌ clipboard error', err)
    Swal.fire({ icon: 'error', text: 'Could not copy tracking number' })
  }
}

// Clear rates if items change
watch(orderLines, () => {
  if (availableRates.value.length > 0) {
    availableRates.value = []
    selectedRate.value = null
    suggestedBox.value = null
  }
}, { deep: true })

// Clear rates if shipping address changes
watch(() => form.shipping, () => {
  if (availableRates.value.length > 0) {
    availableRates.value = []
    selectedRate.value = null
    suggestedBox.value = null
  }
}, { deep: true })



async function voidLabelForCard(label) {
  const result = await Swal.fire({
    title: 'Void Label?',
    text: `Are you sure you want to void the label for ${label.trackingNumber}?`,
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#ef4444',
    cancelButtonColor: '#6b7280',
    confirmButtonText: 'Yes, void it'
  })

  if (result.isConfirmed) {
    try {
      await voidLabel(label.id)
      label.voided = true
      
      // Update the original card in the board state so it persists if modal is reopened
      const list = board.lists.find(l => l.id === form.listId)
      if (list) {
        const card = list.cards.find(c => c.id === form.id)
        if (card && card.shippingLabels) {
          const originalLabel = card.shippingLabels.find(l => l.id === label.id)
          if (originalLabel) {
            originalLabel.voided = true
          }
        }
      }

      Swal.fire('Voided!', 'The label has been voided.', 'success')
    } catch (err) {
      console.error('Failed to void label:', err)
      Swal.fire({
        icon: 'error',
        title: 'Void Failed',
        text: err.response?.data?.error || 'Failed to void label'
      })
    }
  }
}

function validateState(event) {
  let val = event.target.value.toUpperCase()
  // Keep only letters
  val = val.replace(/[^A-Z]/g, '')
  // Max 2 chars
  if (val.length > 2) val = val.slice(0, 2)
  
  form.shipping.state = val
  // Force update if needed (v-model handles it usually, but direct replacement guarantees clean input)
  event.target.value = val
}
</script>

<style scoped>
@keyframes fadeIn { from { opacity:0 } to { opacity:1 } }
.animate-fade-in { animation: fadeIn .3s ease-out }
.dragging { opacity: .6; transform: rotate(3deg) scale(1.02); z-index: 1000; }
.list-drag-over { background: linear-gradient(135deg,#dbeafe,#bfdbfe); transform: scale(1.02); }

.btn-primary {
  background: #83f6b8;
  color: black;
  transition: all .2s ease;
}
.add-card-button:hover{ border-color: #83f6b8; background-color: #ddffed; }
.unread-div{ background-color: #ddffed; }
.notification-icon-div{ background-color: #b2ffd6; padding: 10px; border-radius: 50%; }

/* Sleek toggle switch */
.switch {
  @apply w-full flex items-center gap-3 px-3 py-2 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 transition;
}
.switch-knob {
  position: relative;
  width: 44px;
  height: 26px;
  border-radius: 9999px;
  background: #e5e7eb;
  display: inline-flex;
  align-items: center;
  transition: background .2s ease;
}
.switch-knob::after {
  content: '';
  width: 20px;
  height: 20px;
  border-radius: 9999px;
  background: white;
  box-shadow: 0 1px 2px rgba(0,0,0,.2);
  transform: translateX(3px);
  transition: transform .2s ease;
}
.switch-on { background: #83f6b8 !important; }
.switch-on::after { transform: translateX(21px); }
.switch-label { @apply text-sm font-medium text-gray-800; }

@keyframes subtleFlash {
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0); }       /* red-500 @ 0% */
  50%       { box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.25); } /* faint ring */
}

.card.stale {
  border-color: #fecaca;         /* red-200 border hint */
  animation: subtleFlash 1.6s ease-in-out infinite;
}


</style>
